import type { SizeRange } from 'src/services/orpc/sizeConfig'
import type { AspectRatio } from '../../../constants'

export type AspectRatioWithPresetInfo = AspectRatio & {
  displayMode: 'default' | 'collapsed'
  accessStatus: 'available' | 'membership_required'
  requiredMembershipTier: number
}

/**
 * Normalize a ratio to a portrait-form key string (e.g. "3:5").
 */
export function toPortraitKey(ratio: AspectRatio): string {
  const w = Math.min(ratio.width, ratio.height)
  const h = Math.max(ratio.width, ratio.height)
  return `${w}:${h}`
}

/**
 * Parse a ratio label like "9:16" into an AspectRatio object.
 * Always normalizes to portrait form (width <= height).
 */
export function parseRatioLabel(label: string): AspectRatio {
  const [a, b] = label.split(':').map(Number)
  if (!a || !b) return { label, width: 1, height: 1 }
  // Normalize to portrait (smaller value as width)
  const width = Math.min(a, b)
  const height = Math.max(a, b)
  return { label: `${width}:${height}`, width, height }
}

/**
 * Derive sidebar quick-select ratios from the first visible range's presets.
 * Returns unique ratios in portrait form, sorted by sortOrder.
 * Only includes presets with displayMode === 'default'.
 */
export function deriveSidebarRatios(ranges: SizeRange[]): AspectRatio[] {
  const firstVisible = ranges.find(r => r.displayMode === 'default')
  if (!firstVisible) return []

  const seen = new Set<string>()
  const ratios: AspectRatio[] = []

  const presets = [...firstVisible.presets]
    .filter(p => p.displayMode === 'default')
    .sort((a, b) => a.sortOrder - b.sortOrder)

  for (const preset of presets) {
    const parsed = parseRatioLabel(preset.ratioLabel)
    const key = toPortraitKey(parsed)
    if (seen.has(key)) continue
    seen.add(key)
    ratios.push(parsed)
  }

  return ratios
}

/**
 * Derive ratios from a single range's presets.
 * Filters out hidden presets, deduplicates by portrait key,
 * and preserves displayMode/accessStatus for UI rendering.
 */
export function deriveRatiosForRange(
  range: SizeRange,
): AspectRatioWithPresetInfo[] {
  const seen = new Set<string>()
  const ratios: AspectRatioWithPresetInfo[] = []

  const presets = [...range.presets]
    .filter(p => p.displayMode !== 'hidden')
    .sort((a, b) => a.sortOrder - b.sortOrder)

  for (const preset of presets) {
    const parsed = parseRatioLabel(preset.ratioLabel)
    const key = toPortraitKey(parsed)
    if (seen.has(key)) continue
    seen.add(key)
    ratios.push({
      ...parsed,
      // Safe: 'hidden' is already filtered out above
      displayMode: preset.displayMode as 'default' | 'collapsed',
      accessStatus: preset.accessStatus,
      requiredMembershipTier: preset.requiredMembershipTier,
    })
  }

  return ratios
}

/**
 * Get the default size from the first visible range's defaultPresetId.
 * Returns null if no default preset is configured or no match found.
 */
export function getDefaultSize(
  ranges: SizeRange[],
): { width: number; height: number } | null {
  const firstVisible = ranges.find(r => r.displayMode === 'default')
  if (!firstVisible?.defaultPresetId) return null
  const preset = firstVisible.presets.find(
    p => p.id === firstVisible.defaultPresetId,
  )
  return preset ? { width: preset.width, height: preset.height } : null
}

/**
 * Calculate pixel size from an aspect ratio and a range.
 * Prioritizes exact preset match over calculation from bounds.
 */
export function calculateSizeFromRatioAndRange(
  ratio: AspectRatio,
  range: SizeRange,
): { width: number; height: number } {
  // Normalize ratio to portrait for matching
  const portraitLabel = toPortraitKey(ratio)
  const isLandscape = ratio.width > ratio.height

  // Try to find a matching preset first
  const matchingPreset = range.presets.find(p => {
    const parsed = parseRatioLabel(p.ratioLabel)
    return toPortraitKey(parsed) === portraitLabel
  })

  if (matchingPreset) {
    // Use preset's exact dimensions, swap if orientation differs
    const presetIsPortrait = matchingPreset.width <= matchingPreset.height
    if (isLandscape === presetIsPortrait) {
      return {
        width: matchingPreset.height,
        height: matchingPreset.width,
      }
    }
    return {
      width: matchingPreset.width,
      height: matchingPreset.height,
    }
  }

  // Fallback: calculate from range bounds
  const longestEdge = Math.max(range.maxWidth, range.maxHeight)
  const step = range.step

  let width: number
  let height: number

  if (ratio.width >= ratio.height) {
    width = longestEdge
    height = Math.round((longestEdge * ratio.height) / ratio.width)
  } else {
    height = longestEdge
    width = Math.round((longestEdge * ratio.width) / ratio.height)
  }

  width = Math.floor(width / step) * step
  height = Math.floor(height / step) * step

  return { width, height }
}

/**
 * Find the range whose longest edge is closest to the current size's longest edge.
 */
export function findClosestRangeBySize(
  width: number,
  height: number,
  ranges: SizeRange[],
): SizeRange {
  if (ranges.length === 0) {
    throw new Error('findClosestRangeBySize: ranges must not be empty')
  }

  const longestEdge = Math.max(width, height)
  let closest = ranges[0]
  let minDiff = Infinity

  for (const range of ranges) {
    const rangeLongest = Math.max(range.maxWidth, range.maxHeight)
    const diff = Math.abs(longestEdge - rangeLongest)
    if (diff < minDiff) {
      minDiff = diff
      closest = range
    }
  }

  return closest
}

/**
 * Validate a size against ranges and presets.
 * Returns whether the size is allowed, and whether it requires membership.
 */
export function validateSizeFromRanges(
  width: number,
  height: number,
  ranges: SizeRange[],
): { allowed: boolean; memberOnly: boolean } {
  // Check if size matches any preset (exact match).
  // Prefer free matches over member-only to avoid false memberOnly flags
  // when the same preset appears in both free and member ranges.
  let presetMatch: { allowed: boolean; memberOnly: boolean } | undefined
  for (const range of ranges) {
    for (const preset of range.presets) {
      // Match both orientations — presets are stored portrait-only
      const match =
        (preset.width === width && preset.height === height) ||
        (preset.width === height && preset.height === width)
      if (match) {
        const isFree =
          range.accessStatus === 'available' &&
          preset.accessStatus === 'available'
        if (isFree) return { allowed: true, memberOnly: false }
        presetMatch ??= { allowed: true, memberOnly: true }
      }
    }
  }
  if (presetMatch) return presetMatch

  // Check if size falls within any range's bounds.
  // Prefer free ranges over member-only.
  let boundsMatch: { allowed: boolean; memberOnly: boolean } | undefined
  for (const range of ranges) {
    const inBounds =
      width >= range.minWidth &&
      width <= range.maxWidth &&
      height >= range.minHeight &&
      height <= range.maxHeight &&
      width % range.step === 0 &&
      height % range.step === 0

    if (inBounds) {
      const isFree = range.accessStatus === 'available'
      if (isFree) return { allowed: true, memberOnly: false }
      boundsMatch ??= { allowed: true, memberOnly: true }
    }
  }
  if (boundsMatch) return boundsMatch

  return { allowed: false, memberOnly: false }
}

/**
 * Extract size limits from API ranges for display in UI text.
 * Aggregates across all ranges per tier to get the overall caps.
 */
export function getSizeLimitsFromRanges(ranges: SizeRange[]) {
  if (ranges.length === 0) {
    return { freeMax: 1280, memberMax: 1400, minSize: 512 }
  }

  const baseTier = Math.min(...ranges.map(r => r.requiredMembershipTier))
  const baseRanges = ranges.filter(r => r.requiredMembershipTier === baseTier)
  const memberRanges = ranges.filter(r => r.requiredMembershipTier > baseTier)

  const freeMax = Math.max(
    ...baseRanges.map(r => Math.max(r.maxWidth, r.maxHeight)),
  )
  const memberMax =
    memberRanges.length > 0
      ? Math.max(...memberRanges.map(r => Math.max(r.maxWidth, r.maxHeight)))
      : freeMax
  const minSize = Math.min(
    ...baseRanges.map(r => Math.min(r.minWidth, r.minHeight)),
  )

  return { freeMax, memberMax, minSize }
}
